import { useEffect, useMemo, useState } from 'react';
import { RRule, Weekday } from 'rrule';
import { useForm } from '@frontend/design-system';
import {
  availabilityRecurrenceOptions,
  AvailabilityRecurrenceTypes,
  AvailabilityRuleFormValues,
  MonthlyRecurrenceFormConfig,
  RuleRecurrenceFormConfig,
} from '../../components/availability/availability-form/types';
import { RRuleHandler } from '../../components/availability/availability-rules/organisms/rule-recurrence-form';
import { formRRuleFromAvailabilityRuleFormValues, getFrequencyValuesFromRRule } from '../../rrule-helper';
import { useMemoizedValue } from '../utils/use-memoized-value';

type UseRuleRecurrenceFormBaseValues = {
  recurrenceType?: string;
};

type useRuleRecurrenceFormProps<TFieldProps, TValues extends UseRuleRecurrenceFormBaseValues> = {
  isAvailabilityRule?: boolean;
  locationId: string | undefined;
  getAvailabilityRuleFieldProps: TFieldProps;
  values: TValues;
  rRule?: RRule;
  handleRRuleChange?: RRuleHandler;
};

export const useRuleRecurrenceForm = <
  TFieldProps,
  TValues extends UseRuleRecurrenceFormBaseValues = AvailabilityRuleFormValues
>({
  isAvailabilityRule,
  values: formValuesProp,
  getAvailabilityRuleFieldProps,
  rRule,
  handleRRuleChange,
}: useRuleRecurrenceFormProps<TFieldProps, TValues>) => {
  const recurrenceType = isAvailabilityRecurrenceType(formValuesProp.recurrenceType)
    ? formValuesProp.recurrenceType
    : undefined;
  const {
    frequency,
    frequencyType,
    monthlyFrequencyType,
    monthlyFrequencyDay,
    monthlyFrequencyWeek,
    monthlyFrequencyWeekday,
    weekdays,
  } = useMemo(() => getFrequencyValuesFromRRule(rRule), [rRule]);
  const [daysOfWeek, setDaysOfWeek] = useState<Weekday[]>(weekdays);
  const [repeatOn, setRepeatOn] = useState<Weekday[]>(weekdays);

  const { values: recurrenceFormValuesProp, getFieldProps: getRecurrenceFormFieldProps } = useForm<
    RuleRecurrenceFormConfig,
    keyof RuleRecurrenceFormConfig
  >({
    fields: {
      repeatFrequency: { type: 'dropdown', value: frequency, required: recurrenceType === 'CUSTOM' },
      repeatFrequencyType: { type: 'dropdown', value: frequencyType, required: recurrenceType === 'CUSTOM' },
    },
  });

  const { values: monthlyRecurrenceFormValuesProp, getFieldProps: getMonthlyRecurrenceFormFieldProps } = useForm<
    MonthlyRecurrenceFormConfig,
    keyof MonthlyRecurrenceFormConfig
  >({
    fields: {
      monthlyFrequencyType: {
        type: 'radio',
        value: monthlyFrequencyType,
        required: recurrenceFormValuesProp.repeatFrequencyType === 'months',
      },
      monthlyFrequencyDay: { type: 'dropdown', value: monthlyFrequencyDay },
      monthlyFrequencyWeekday: { type: 'dropdown', value: monthlyFrequencyWeekday },
      monthlyFrequencyWeek: { type: 'dropdown', value: monthlyFrequencyWeek },
    },
  });

  const formValues = useMemoizedValue(formValuesProp);
  const recurrenceFormValues = useMemoizedValue(recurrenceFormValuesProp);
  const monthlyRecurrenceFormValues = useMemoizedValue(monthlyRecurrenceFormValuesProp);

  useEffect(() => {
    if (handleRRuleChange) {
      const rrule = formRRuleFromAvailabilityRuleFormValues({
        isAvailabilityRule,
        recurrenceType,
        recurrenceFormValues,
        monthlyRecurrenceFormValues,
        daysOfWeek,
        repeatOn,
      });
      handleRRuleChange(rrule);
    }
  }, [
    recurrenceType,
    recurrenceFormValues,
    monthlyRecurrenceFormValues,
    daysOfWeek,
    repeatOn,
    handleRRuleChange,
    isAvailabilityRule,
  ]);

  return {
    recurrenceType,
    recurrenceFormValues,
    daysOfWeek,
    repeatOn,
    rRule,
    formValues,
    setDaysOfWeek,
    setRepeatOn,
    getRecurrenceFormFieldProps,
    getMonthlyRecurrenceFormFieldProps,
    getAvailabilityRuleFieldProps,
  };
};

const isAvailabilityRecurrenceType = (value: string | undefined): value is AvailabilityRecurrenceTypes => {
  return availabilityRecurrenceOptions.some((option) => option.id === value) || value === 'SINGLE_OCCURRENCE';
};
